// src/components/PrivacyPolicy.jsx
import React, { useMemo } from "react";

import NoticeBox from "./common/NoticeBox";

const PrivacyPolicy = () => {
  const header = useMemo(
    () => ({
      badge: "Legal Information",
      title: "Privacy Policy",
      description:
        "Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.",
      lastUpdated: "Last Updated: February 11, 2026",
    }),
    []
  );

  const sections = useMemo(
    () => [
      {
        id: 1,
        title: "Information We Collect",
        items: [
          {
            subtitle: "Personal Information",
            text: "We collect information that you provide directly to us, including your name, email address, postal address, phone number, and payment information when you create an account, make a purchase, or communicate with us.",
          },
          {
            subtitle: "Automatically Collected Information",
            text: "We automatically collect certain information about your device when you use our website, including your IP address, browser type, operating system, referring URLs, and information about your usage of our website.",
          },
          {
            subtitle: "Cookies and Tracking Technologies",
            text: "We use cookies, web beacons, and similar tracking technologies to collect information about your browsing activities and to personalize your experience on our website.",
          },
        ],
      },
      {
        id: 2,
        title: "How We Use Your Information",
        items: [
          {
            subtitle: "Order Processing",
            text: "We use your information to process and fulfill your orders, communicate with you about your orders, and provide customer support.",
          },
          {
            subtitle: "Service Improvement",
            text: "We analyze usage patterns and feedback to improve our website, products, and services, and to develop new features and offerings.",
          },
          {
            subtitle: "Marketing Communications",
            text: "With your consent, we may send you promotional emails about new products, special offers, and other information we think you may find interesting. You can opt-out at any time.",
          },
          {
            subtitle: "Legal Compliance",
            text: "We may use your information to comply with applicable laws, regulations, legal processes, or governmental requests.",
          },
        ],
      },
      {
        id: 3,
        title: "Information Sharing and Disclosure",
        items: [
          {
            subtitle: "Service Providers",
            text: "We may share your information with third-party service providers who perform services on our behalf, such as payment processing, shipping, marketing, and analytics.",
          },
          {
            subtitle: "Business Transfers",
            text: "If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change in ownership or control.",
          },
          {
            subtitle: "Legal Requirements",
            text: "We may disclose your information if required to do so by law or in response to valid requests by public authorities.",
          },
          {
            subtitle: "With Your Consent",
            text: "We may share your information with third parties when we have your explicit consent to do so.",
          },
        ],
      },
      {
        id: 4,
        title: "Data Security",
        items: [
          {
            subtitle: "Security Measures",
            text: "We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.",
          },
          {
            subtitle: "Encryption",
            text: "We use SSL/TLS encryption to protect sensitive information transmitted online. All payment information is encrypted during transmission and storage.",
          },
          {
            subtitle: "Access Controls",
            text: "We restrict access to personal information to employees, contractors, and agents who need to know that information to process it for us and who are subject to strict contractual confidentiality obligations.",
          },
          {
            subtitle: "Regular Audits",
            text: "We regularly review and update our security practices to ensure the ongoing protection of your information.",
          },
        ],
      },
      {
        id: 5,
        title: "Your Rights and Choices",
        items: [
          {
            subtitle: "Access and Update",
            text: "You have the right to access, update, or correct your personal information at any time by logging into your account or contacting us.",
          },
          {
            subtitle: "Delete Your Account",
            text: "You may request deletion of your account and personal information. Please note that we may retain certain information as required by law or for legitimate business purposes.",
          },
          {
            subtitle: "Opt-Out of Marketing",
            text: "You can opt-out of receiving promotional emails by clicking the unsubscribe link in any email or by updating your communication preferences in your account settings.",
          },
          {
            subtitle: "Cookie Preferences",
            text: "You can control cookies through your browser settings. Please note that disabling cookies may affect the functionality of our website.",
          },
          {
            subtitle: "Do Not Track",
            text: "Some browsers have a 'Do Not Track' feature. At this time, we do not respond to Do Not Track signals.",
          },
        ],
      },
      {
        id: 6,
        title: "Children's Privacy",
        items: [
          {
            subtitle: "Age Restriction",
            text: "Our services are not directed to children under the age of 13. We do not knowingly collect personal information from children under 13.",
          },
          {
            subtitle: "Parental Rights",
            text: "If we learn that we have collected personal information from a child under 13, we will take steps to delete such information as soon as possible. If you believe we have collected information from a child under 13, please contact us immediately.",
          },
        ],
      },
      {
        id: 7,
        title: "International Data Transfers",
        items: [
          {
            subtitle: "Cross-Border Transfers",
            text: "Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that are different from the laws of your country.",
          },
          {
            subtitle: "Data Protection",
            text: "We ensure that such transfers are conducted in accordance with applicable data protection laws and that your information receives an adequate level of protection.",
          },
        ],
      },
      {
        id: 8,
        title: "Changes to This Policy",
        items: [
          {
            subtitle: "Updates",
            text: "We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the 'Last Updated' date.",
          },
          {
            subtitle: "Your Continued Use",
            text: "Your continued use of our services after any changes to this Privacy Policy constitutes your acceptance of such changes.",
          },
        ],
      },
    ],
    []
  );

  const notice = useMemo(
    () => ({
      type: "warning",
      icon: "ph-info",
      title: "Your Privacy Matters",
      message:
        "We are committed to protecting your privacy and handling your data with care. If you have any questions or concerns about how we use your information, or if you would like to exercise any of your privacy rights, please contact us.",
    }),
    []
  );

  return (
    <section className="privacy-policy">
      <style>
        {`
          .privacy-policy {
            padding: 40px 0;
            background: #fafafa;
          }

          .privacy-policy .policy-wrapper {
            margin: 0 auto;
          }

          .privacy-policy .policy-header {
            text-align: center;
            margin-bottom: 30px;
          }

          .privacy-policy .policy-badge {
            display: inline-block;
            font-size: 13px;
            font-weight: 600;
            color: #0d6efd;
            background: #eef5ff;
            padding: 6px 14px;
            border-radius: 50px;
            margin-bottom: 12px;
          }

          .privacy-policy .policy-title {
            font-size: 34px;
            line-height: 1.2;
            margin-bottom: 10px;
            color: #222;
          }

          .privacy-policy .policy-description {
            max-width: 620px;
            margin: 0 auto 10px;
            font-size: 15px;
            line-height: 1.6;
            color: #666;
          }

          .privacy-policy .last-updated {
            font-size: 13px;
            color: #888;
          }

          .privacy-policy .policy-section {
            background: #fff;
            border: 1px solid #eee;
            border-radius: 12px;
            padding: 22px;
            margin-bottom: 16px;
          }

          .privacy-policy .policy-section-title {
            font-size: 20px;
            line-height: 1.3;
            margin-bottom: 16px;
            color: #222;
            font-weight: 700;
          }

          .privacy-policy .policy-item {
            margin-bottom: 14px;
          }

          .privacy-policy .policy-item:last-child {
            margin-bottom: 0;
          }

          .privacy-policy .policy-item-title {
            font-size: 16px;
            margin-bottom: 6px;
            color: #333;
            font-weight: 600;
          }

          .privacy-policy .policy-item-text {
            font-size: 14px;
            line-height: 1.6;
            color: #666;
            margin-bottom: 0;
          }

          .privacy-policy .notice-small {
            margin-top: 24px;
          }

          @media (max-width: 575px) {
            .privacy-policy {
              padding: 30px 0;
            }

            .privacy-policy .policy-title {
              font-size: 28px;
            }

            .privacy-policy .policy-section {
              padding: 18px;
            }

            .privacy-policy .policy-section-title {
              font-size: 18px;
            }
          }
        `}
      </style>

      <div className="container">
        <div className="policy-wrapper">
          <div className="policy-header">
            <span className="policy-badge">{header.badge}</span>

            <h1 className="policy-title">{header.title}</h1>

            <p className="policy-description">{header.description}</p>

            <div className="last-updated">{header.lastUpdated}</div>
          </div>

          {sections.map((section) => (
            <div
              key={section.id}
              id={`section-${section.id}`}
              className="policy-section"
            >
              <h3 className="policy-section-title">{section.title}</h3>

              <div className="policy-section-content">
                {section.items.map((item, index) => (
                  <div key={index} className="policy-item">
                    <h4 className="policy-item-title">{item.subtitle}</h4>
                    <p className="policy-item-text">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <NoticeBox
            type={notice.type}
            icon={notice.icon}
            title={notice.title}
            message={notice.message}
            className="notice-small"
          />
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;